import { userService } from "@/app/api/user";
import type {
  CompanyQuestionTemplate,
  CompanyManageDetail,
  CompanyManageListItem,
  CompanyDefaultsManageResponse,
  UpdateCompanyDefaultsManagePayload,
  UpdateCompanyManagePayload,
  UpsertCompanyQuestionTemplatePayload,
} from "@/types/company-manage";

export const getCompanyManageList = async (): Promise<
  CompanyManageListItem[]
> => {
  try {
    const response = await userService.axiosWithUserAuth.get<
      CompanyManageListItem[]
    >("/company/cx/manage/all");
    return response.data;
  } catch (error) {
    console.error("Error fetching company manage list:", error);
    throw error;
  }
};

export const createCompanyManage = async (name: string): Promise<void> => {
  try {
    await userService.axiosWithUserAuth.post("/company/cx/manage", { name });
  } catch (error) {
    console.error("Error creating company:", error);
    throw error;
  }
};

export const getCompanyManageDetail = async (
  companyId: string,
): Promise<CompanyManageDetail> => {
  try {
    const response =
      await userService.axiosWithUserAuth.get<CompanyManageDetail>(
        `/company/cx/manage/${companyId}`,
      );
    return response.data;
  } catch (error) {
    console.error("Error fetching company manage detail:", error);
    throw error;
  }
};

export const updateCompanyManage = async (
  companyId: string,
  payload: UpdateCompanyManagePayload,
): Promise<void> => {
  try {
    await userService.axiosWithUserAuth.patch(
      `/company/cx/manage/${companyId}`,
      payload,
    );
  } catch (error) {
    console.error("Error updating company:", error);
    throw error;
  }
};

export const getCompanyQuestionTemplate = async (
  companyId: string,
): Promise<CompanyQuestionTemplate | null> => {
  const response =
    await userService.axiosWithUserAuth.get<CompanyQuestionTemplate | null>(
      `/company/cx/manage/${companyId}/question-template`,
    );

  return response.data;
};

export const upsertCompanyQuestionTemplate = async (
  companyId: string,
  payload: UpsertCompanyQuestionTemplatePayload,
): Promise<CompanyQuestionTemplate> => {
  const response =
    await userService.axiosWithUserAuth.put<CompanyQuestionTemplate>(
      `/company/cx/manage/${companyId}/question-template`,
      payload,
    );

  return response.data;
};

/*
 * TODO(AS-1925): Company-default AI generation is intentionally disabled for
 * this PR. Re-enable these clients alongside the backend wrapper endpoints.
 *
 * const generateCompanyQuestionTemplateField = async <T extends GeneratableFields>(
 *   companyId: string,
 *   field: T,
 *   generateInput: GenerateCompetencyFieldBody<T>,
 * ): Promise<GenerateResponse<T>> => {
 *   const response = await userService.axiosWithUserAuth.post<GenerateResponse<T>>(
 *     `/company/cx/manage/${companyId}/question-template/generate`,
 *     generateInput,
 *   );
 *
 *   return response.data;
 * };
 *
 * export const generateCompanyQuestionTemplateFieldWithRetry = async <
 *   T extends GeneratableFields,
 * >(
 *   companyId: string,
 *   field: T,
 *   generateInput: GenerateCompetencyFieldBody<T>,
 * ) =>
 *   generateCompetencyFieldWithRetry(field, generateInput, (retryField, input) =>
 *     generateCompanyQuestionTemplateField(companyId, retryField, input),
 *   );
 *
 * export const validateCompanyQuestionTemplateStep = async (
 *   companyId: string,
 *   stepData: ValidateCompetencyStepInput,
 * ): Promise<ValidateCompetencyStepResponse> =>
 *   requestWithRetry(async () => {
 *     const response =
 *       await userService.axiosWithUserAuth.post<ValidateCompetencyStepResponse>(
 *         `/company/cx/manage/${companyId}/question-template/validate`,
 *         stepData,
 *       );
 *
 *     return response.data;
 *   });
 */

export const updateCompanyDefaultsManage = async (
  companyId: string,
  payload: UpdateCompanyDefaultsManagePayload,
): Promise<CompanyDefaultsManageResponse> => {
  const response =
    await userService.axiosWithUserAuth.put<CompanyDefaultsManageResponse>(
      `/company/cx/manage/${companyId}/defaults`,
      payload,
    );

  return response.data;
};

export const deactivateCompany = async (companyId: string): Promise<void> => {
  try {
    await userService.axiosWithUserAuth.delete(
      `/company/cx/manage/${companyId}`,
    );
  } catch (error) {
    console.error("Error deactivating company:", error);
    throw error;
  }
};

export const reactivateCompany = async (companyId: string): Promise<void> => {
  try {
    await userService.axiosWithUserAuth.patch(
      `/company/cx/manage/${companyId}/reactivate`,
    );
  } catch (error) {
    console.error("Error reactivating company:", error);
    throw error;
  }
};
